<template>
    <ObjectsTextTooltip
        v-if="product.isPremiumExclusive && !authUser?.isPremium"
        :text="$t('product.premiumFromXPerMonth')"
        placement="top"
    >
        <nuxt-link to="/pilulka-premium" class="product-card-add-to-basket__premium-button">
            {{ $t('product.moreAboutPilulkaPremium') }}
        </nuxt-link>
    </ObjectsTextTooltip>

    <div v-else class="product-card-add-to-basket__container">
        <template v-if="basketAmount > 0">
            <img
                :src="`/nuxt-assets/icons/minus-new.svg?v=1`"
                :alt="$t('productDetail.removeFromBasket')"
                class="product-card-add-to-basket__button"
                data-cy="add-cart__change-minus"
                tabindex="0"
                @keydown.enter="changeProductAmount(basketAmount - 1)"
                @click.prevent="changeProductAmount(basketAmount - 1)"
            />

            <input
                v-model="basketAmount"
                data-cy="inputCounterProduct"
                class="product-card-add-to-basket__input"
                type="number"
                min="1"
                max="99"
                @change="inputAmountChanged"
            />
        </template>

        <ObjectsTextTooltip
            v-if="basketAmount >= maxAmountPerOrderFinal"
            :text="basketAmount === 0 ? $t('product.insertToCart') : maxAmountPerOrderText"
            placement="top"
        >
            <img
                :src="`/nuxt-assets/icons/plus-new.svg?v=1`"
                :alt="$t('productDetail.addToBasket')"
                class="product-card-add-to-basket__button product-card-add-to-basket__button--disabled"
                data-cy="add-cart__change-plus"
            />
        </ObjectsTextTooltip>
        <ObjectsTextTooltip
            v-else-if="basketAmount === 0"
            :text="basketAmount === 0 ? $t('product.insertToCart') : maxAmountPerOrderText"
            placement="top"
        >
            <button
                class="product-card-add-to-basket__button"
                data-cy="add-cart__change-plus"
                :aria-label="$t('productDetail.addToBasket')"
                @click.prevent="changeProductAmount(basketAmount + 1)"
            >
                <img :src="`/nuxt-assets/icons/plus-new.svg?v=1`" alt="" aria-hidden="true" />
            </button>
        </ObjectsTextTooltip>
        <img
            v-else
            :src="`/nuxt-assets/icons/plus-new.svg?v=1`"
            :alt="$t('productDetail.addToBasket')"
            class="product-card-add-to-basket__button"
            data-cy="add-cart__change-plus"
            tabindex="0"
            @keydown.enter="changeProductAmount(basketAmount + 1)"
            @click.prevent="changeProductAmount(basketAmount + 1)"
        />
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { PropType } from 'vue'
import { useBasketStore } from '~/stores/basket'
import { addProductToBasket } from '~/utils/addProductToBasket'
import Product from '~/models/Product/Product'
import FullProduct from '~/models/Product/FullProduct'
import BasketProduct from '~/models/Basket/BasketProduct'

const props = defineProps({
    product: {
        type: Object as PropType<Product>,
        default: null,
    },
    fullProduct: {
        type: Object as PropType<FullProduct>,
        default: null,
    },
    basketProduct: {
        type: Object as PropType<BasketProduct>,
        default: null,
    },

    // product properties
    productId: {
        type: Number,
        required: true,
    },
    productPrice: {
        type: Number,
        required: true,
    },
    isPreorder: {
        type: Boolean,
        required: true,
    },
    maxAmountPerOrder: {
        type: Number,
        required: true,
    },
    hasSubstitutionModal: {
        type: Boolean,
        required: true,
    },

    // general properties
    buttonClass: {
        type: String,
        default: 'btn-primary',
    },
    addBasketAlign: {
        type: String,
        default: 'text-end',
    },
    showPreBasket: {
        type: Boolean,
        default: true,
    },
    alertCartShow: {
        type: Boolean,
        default: true,
    },

    // GTM properties
    listPosition: {
        type: Number,
        default: null,
    },
    listTitle: {
        type: String,
        default: '',
    },
    listType: {
        type: String,
        default: '',
    },
    itemListName: {
        type: String,
        default: '',
    },
    carouselName: {
        type: String,
        default: '',
    },
    showActualStateMode: {
        type: String,
        default: 'hidden',
    },
    addonSaleBehavior: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['productAddedToBasket'])

const maxAmountDefault = ref(99)
const loading = ref(false)
const i18n = useI18n()

onMounted(() => {
    useNuxtApp().$bus.$on('productAddedToBasket', () => {
        loading.value = false
    })
})

const maxAmountPerOrderText = computed(() => {
    return props.maxAmountPerOrder < maxAmountDefault.value
        ? i18n.t('basic.maxAmountPerOrder')
        : i18n.t('basic.maxAmount', { n: props.maxAmountPerOrder })
})

const maxAmountPerOrderFinal = computed(() => {
    return props.maxAmountPerOrder || maxAmountDefault.value
})

const basketAmount = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.productAmounts[props.productId] || 0
})

const authUser = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.authUser
})

const inputAmountChanged = (event: any): void => {
    changeProductAmount(parseInt(event.target.value))
}

const changeProductAmount = (amount: number): void => {
    const { listTitle, listType, listPosition, itemListName, carouselName, productId, showPreBasket } = props

    // substitutions removed due to legal issues - IS-6888
    /* if (this.basketAmount === 0 && this.hasSubstitutionModal) {
        useNuxtApp().$bus.$emit('showProductSubstitutionsModal')
        return
    } */

    addProductToBasket({
        product: props.product,
        fullProduct: props.fullProduct,
        basketProduct: props.basketProduct,
        productId,
        amount,
        showPreBasket,
        itemListName,
        carouselName,
        listTitle,
        listType,
        listPosition,
        productIdReferrer: productIdReferrer?.value,
        addonSaleBehavior: props.addonSaleBehavior,
    })

    let url = ''

    if (props.product) {
        url = props.product.url
    } else if (props.fullProduct) {
        url = props.fullProduct.url
    } else if (props.basketProduct) {
        url = props.basketProduct.url
    }

    loading.value = true
    emit('productAddedToBasket', url)
}

const productIdReferrer = inject<Ref<number | null> | null>('productIdReferrer', null)
</script>

<style lang="scss" scoped>
.product-card-add-to-basket {
    &__premium-button {
        background: $color-cta-extra-light;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        padding: 6px 10px;
        display: inline-block;

        &:hover {
            background: darken($color-cta-extra-light, 2%);
        }
    }

    &__button {
        margin: 0;
        padding: 0;
        max-width: none;
        cursor: pointer;
        width: 32px;
        height: 32px;
        border: 3px solid $color-white;
        border-radius: 100%;
        box-sizing: content-box;

        @container (min-width: 250px) {
            width: 40px;
            height: 40px;
        }

        &--disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        img {
            width: 100%;
        }
    }

    &__input {
        background: transparent;
        text-align: center;
        color: $color-cta;
        font-weight: 800;
        width: 30px;
        -moz-appearance: textfield;

        @include screen-md {
            width: 50px;
        }

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }

    &__container {
        display: flex;
        justify-content: end;
        //background: #F2F2E9;
        background: #fff;
        border-radius: 52px;
        overflow: hidden;
    }
}
</style>
